import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  marketplaceCapabilityApi,
  type AtomicCapability,
  type CapabilityDeliverable,
  type CapabilityOwnership,
  type CapabilityTemplateField,
  type CapabilityValidation,
  type DependencyMatrixEntry,
  type FeatureInventoryEntry,
  type HierarchyNode,
  type MCADashboard,
  type MCABibleStatus,
} from '../../api/marketplace-capability-architecture';

type Tab = 'overview' | 'hierarchy' | 'capabilities' | 'template' | 'dependencies' | 'ownership' | 'inventory' | 'deliverables' | 'status';
type Domain = 'listing' | 'advertising' | 'fba' | 'profit' | 'ai' | 'experiment' | 'executive';
type Theme = 'light' | 'dark';

const domainLabels: Record<Domain, string> = {
  listing: 'Listing Intelligence',
  advertising: 'Advertising Intelligence',
  fba: 'FBA Intelligence',
  profit: 'Profit Intelligence',
  ai: 'Marketplace AI',
  experiment: 'Growth Experiment',
  executive: 'Executive Intelligence',
};

export function MarketplaceCapabilityArchitecturePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [domain, setDomain] = useState<Domain>('listing');
  const [dashboard, setDashboard] = useState<MCADashboard | null>(null);
  const [hierarchy, setHierarchy] = useState<HierarchyNode[]>([]);
  const [capabilities, setCapabilities] = useState<Record<Domain, AtomicCapability[]>>({
    listing: [], advertising: [], fba: [], profit: [], ai: [], experiment: [], executive: [],
  });
  const [template, setTemplate] = useState<CapabilityTemplateField[]>([]);
  const [dependencies, setDependencies] = useState<DependencyMatrixEntry[]>([]);
  const [ownership, setOwnership] = useState<CapabilityOwnership[]>([]);
  const [features, setFeatures] = useState<FeatureInventoryEntry[]>([]);
  const [deliverables, setDeliverables] = useState<CapabilityDeliverable[]>([]);
  const [status, setStatus] = useState<MCABibleStatus | null>(null);
  const [validation, setValidation] = useState<CapabilityValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, hier, listing, advertising, fba, profit, ai, experiment, executive, tmpl, deps, own, inv, del, st] = await Promise.all([
        marketplaceCapabilityApi.dashboard(),
        marketplaceCapabilityApi.hierarchy(),
        marketplaceCapabilityApi.listingCapabilities(),
        marketplaceCapabilityApi.advertisingCapabilities(),
        marketplaceCapabilityApi.fbaCapabilities(),
        marketplaceCapabilityApi.profitCapabilities(),
        marketplaceCapabilityApi.aiCapabilities(),
        marketplaceCapabilityApi.experimentCapabilities(),
        marketplaceCapabilityApi.executiveCapabilities(),
        marketplaceCapabilityApi.capabilityTemplate(),
        marketplaceCapabilityApi.dependencyMatrix(),
        marketplaceCapabilityApi.ownership(),
        marketplaceCapabilityApi.featureInventory(),
        marketplaceCapabilityApi.deliverables(),
        marketplaceCapabilityApi.status(),
      ]);
      setDashboard(dash);
      setHierarchy(hier);
      setCapabilities({ listing, advertising, fba, profit, ai, experiment, executive });
      setTemplate(tmpl);
      setDependencies(deps);
      setOwnership(own);
      setFeatures(inv);
      setDeliverables(del);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load marketplace capability architecture');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validateCapability = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await marketplaceCapabilityApi.validateCapability({
        capability_key: 'title_intelligence',
        attrs: {
          mission_defined: true, business_value: true, measurable_kpis: true,
          dependencies_mapped: true, apis_defined: true, events_defined: true,
          permissions_defined: true, deployable_unit: true, testing_strategy: true, release_version: true,
        },
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Capability passes MCA-000001 definition of done' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-amber-900/40 bg-slate-900' : 'border-amber-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-amber-50/20 text-slate-900';
  const accent = dark ? 'text-amber-300' : 'text-amber-700';
  const tabActive = 'bg-amber-600 text-white';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'hierarchy', label: 'Hierarchy' },
    { id: 'capabilities', label: 'Capabilities' },
    { id: 'template', label: 'Template' },
    { id: 'dependencies', label: 'Dependencies' },
    { id: 'ownership', label: 'Ownership' },
    { id: 'inventory', label: 'Feature Inventory' },
    { id: 'deliverables', label: 'Deliverables' },
    { id: 'status', label: 'Status' },
  ];

  const activeCaps = capabilities[domain];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>MCA-000001</p>
          <h1 className="text-2xl font-semibold">Marketplace Capability Architecture</h1>
          <p className={`text-sm ${muted}`}>
            Complete business capability map, product capability model, module boundaries, feature inventory, dependency matrix and engineering blueprint
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/marketplace-screen-bible" className={`${accent} hover:underline`}>UXS-000001 Screens</Link>
            <span className={muted}>→</span>
            <Link to="/platform/enterprise-design-system" className={`${accent} hover:underline`}>EDS-000001</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light' : 'Dark'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Capability-Driven Engineering Blueprint</h2>
            <p className={`mt-1 text-sm ${muted}`}>Depends on: {dashboard.depends_on.join(' · ')}</p>
            <p className={`mt-3 text-sm ${muted}`}>
              Every marketplace feature belongs to one clearly defined business capability.
              Every capability becomes an independently deployable engineering unit with measurable business outcomes.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Hierarchy Nodes', value: dashboard.hierarchy_nodes },
              { label: 'Atomic Capabilities', value: dashboard.atomic_capabilities },
              { label: 'Listing', value: dashboard.listing_capabilities },
              { label: 'Advertising', value: dashboard.advertising_capabilities },
              { label: 'FBA', value: dashboard.fba_capabilities },
              { label: 'Profit', value: dashboard.profit_capabilities },
              { label: 'Marketplace AI', value: dashboard.ai_capabilities },
              { label: 'Experiments', value: dashboard.experiment_capabilities },
              { label: 'Executive', value: dashboard.executive_capabilities },
              { label: 'Dependencies', value: dashboard.dependency_entries },
              { label: 'Ownership', value: dashboard.ownership_assignments },
              { label: 'Features', value: dashboard.feature_inventory },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'hierarchy' && (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((level) => {
            const nodes = hierarchy.filter((n) => n.level === level);
            if (nodes.length === 0) return null;
            return (
              <section key={level}>
                <h2 className={`mb-2 text-sm font-semibold uppercase ${accent}`}>Level {level}</h2>
                <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                  {nodes.map((n) => (
                    <div key={n.key} className={`rounded-xl border p-4 ${card}`}>
                      <h3 className="font-semibold">{n.name}</h3>
                      {n.parent_key && <p className={`text-xs ${muted}`}>Parent: {n.parent_key}</p>}
                      {n.description && <p className={`mt-1 text-sm ${muted}`}>{n.description}</p>}
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {tab === 'capabilities' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {(Object.keys(domainLabels) as Domain[]).map((d) => (
              <button key={d} type="button" onClick={() => setDomain(d)} className={`rounded-lg px-3 py-1.5 text-sm ${domain === d ? tabActive : `${card} border`}`}>
                {domainLabels[d]} ({capabilities[d].length})
              </button>
            ))}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {activeCaps.map((c) => (
              <div key={c.key} className={`rounded-xl border p-4 ${card}`}>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold">{c.name}</h3>
                  {c.deployable && <span className="shrink-0 rounded bg-indigo-100 px-2 py-0.5 text-xs text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">Deployable</span>}
                </div>
                <p className={`mt-2 text-sm ${muted}`}>{c.purpose}</p>
                {c.kpis && c.kpis.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {c.kpis.map((k) => <span key={k} className="rounded bg-amber-100 px-2 py-0.5 text-xs dark:bg-amber-900">{k}</span>)}
                  </div>
                )}
                {c.target_persona && <p className={`mt-2 text-xs ${muted}`}>Persona: {c.target_persona}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'template' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-sm ${card}`}>
            <thead>
              <tr className={`border-b text-left ${dark ? 'border-slate-800' : 'border-slate-200'}`}>
                <th className="px-4 py-2">Field</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Required</th>
                <th className="px-4 py-2">Description</th>
              </tr>
            </thead>
            <tbody>
              {template.map((f) => (
                <tr key={f.key} className={`border-b ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <td className="px-4 py-2 font-medium">{f.name}</td>
                  <td className={`px-4 py-2 ${muted}`}>{f.category}</td>
                  <td className="px-4 py-2">{f.required ? 'Yes' : 'No'}</td>
                  <td className={`px-4 py-2 text-xs ${muted}`}>{f.description ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'dependencies' && (
        <div className="space-y-3">
          {dependencies.map((d) => (
            <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-semibold">{d.capability_key}</h3>
              <p className={`mt-1 text-sm ${muted}`}>Depends on: {d.depends_on.join(', ') || '—'}</p>
              {d.shared_services && d.shared_services.length > 0 && (
                <p className={`mt-1 text-xs ${muted}`}>Shared services: {d.shared_services.join(', ')}</p>
              )}
              {d.shared_ai && d.shared_ai.length > 0 && (
                <p className={`mt-1 text-xs ${muted}`}>Shared AI: {d.shared_ai.join(', ')}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'ownership' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-sm ${card}`}>
            <thead>
              <tr className={`border-b text-left ${dark ? 'border-slate-800' : 'border-slate-200'}`}>
                <th className="px-4 py-2">Domain</th>
                <th className="px-4 py-2">Business Owner</th>
                <th className="px-4 py-2">Technical Owner</th>
                <th className="px-4 py-2">AI Owner</th>
                <th className="px-4 py-2">KPI Owner</th>
              </tr>
            </thead>
            <tbody>
              {ownership.map((o) => (
                <tr key={o.key} className={`border-b ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <td className="px-4 py-2 font-medium capitalize">{o.domain}</td>
                  <td className={`px-4 py-2 ${muted}`}>{o.business_owner}</td>
                  <td className={`px-4 py-2 ${muted}`}>{o.technical_owner}</td>
                  <td className={`px-4 py-2 ${muted}`}>{o.ai_owner ?? '—'}</td>
                  <td className={`px-4 py-2 ${muted}`}>{o.kpi_owner ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'inventory' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-sm ${card}`}>
            <thead>
              <tr className={`border-b text-left ${dark ? 'border-slate-800' : 'border-slate-200'}`}>
                <th className="px-4 py-2">Feature</th>
                <th className="px-4 py-2">Capability</th>
                <th className="px-4 py-2">Domain</th>
                <th className="px-4 py-2">Release</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {features.map((f) => (
                <tr key={f.key} className={`border-b ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <td className="px-4 py-2 font-medium">{f.feature}</td>
                  <td className={`px-4 py-2 font-mono text-xs ${muted}`}>{f.capability_key}</td>
                  <td className={`px-4 py-2 capitalize ${muted}`}>{f.domain}</td>
                  <td className={`px-4 py-2 ${muted}`}>{f.release ?? '—'}</td>
                  <td className="px-4 py-2">
                    <span className={`rounded px-2 py-0.5 text-xs ${f.status === 'complete' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200' : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'}`}>
                      {f.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'deliverables' && (
        <div className="grid gap-3 md:grid-cols-2">
          {deliverables.map((d) => (
            <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{d.name}</h3>
                <span className={`rounded px-2 py-0.5 text-xs ${d.status === 'complete' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200' : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'}`}>
                  {d.status}
                </span>
              </div>
              {d.description && <p className={`mt-1 text-sm ${muted}`}>{d.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'status' && status && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">{status.status}</span>
            {status.capability_driven && <p className={`mt-3 text-sm ${muted}`}>Every feature belongs to one capability</p>}
            {status.engineering_ready && <p className={`text-sm ${muted}`}>Engineering-ready capability architecture complete</p>}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Validate Capability</h3>
            <button type="button" disabled={busy} onClick={validateCapability} className={`mt-4 rounded-lg px-4 py-2 text-sm text-white ${tabActive} disabled:opacity-50`}>
              {busy ? 'Validating…' : 'Run Sample Validation'}
            </button>
            {validation && (
              <pre className="mt-3 overflow-x-auto rounded-lg bg-slate-100 p-3 text-xs dark:bg-slate-800">{JSON.stringify(validation, null, 2)}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
